// REST client for the Realtime server. Every request carries the Firebase ID token as a
// Bearer when a user is signed in — realtime requires one on every /api/* route since
// 2026-10-25 (http-auth.ts); `auth: false` opts a call out. Each method unwraps the server envelope; richer
// shape-mapping to Legend render models lives in mappers.ts (applied by the callers/hooks).
import { config } from './config';
import { getIdToken } from './firebase';

type ReqOpts = {
  method?: string;
  auth?: boolean;
  params?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
};

async function req(path: string, opts: ReqOpts = {}): Promise<any> {
  const url = new URL(config.apiUrl + path);
  if (opts.params) {
    for (const [k, v] of Object.entries(opts.params)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.auth !== false) {
    const token = await getIdToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    let detail = '';
    try { detail = JSON.stringify(await res.json()); } catch { /* ignore */ }
    throw new Error(`${opts.method ?? 'GET'} ${path} → ${res.status} ${detail}`);
  }
  return res.json();
}

export const api = {
  // symbols
  getActiveSymbols: () =>
    req('/api/symbols/active').then((r) => r.results ?? r.symbols ?? r ?? []),
  // Screener seed — current surge snapshot, strongest first. `direction` picks
  // the lane: omitted/UP = buy-led surges (the default the long feed relies on),
  // DOWN = sell-led flushes, ALL = both.
  getWatchlist: (params: { bucket?: string; limit?: number; direction?: 'UP' | 'DOWN' | 'ALL' } = {}) =>
    req('/api/watchlist', { params }).then((r) => r.results ?? r ?? []),
  searchSymbols: (query: string, limit = 20) =>
    req('/api/symbols/search', { params: { query, limit } }).then((r) => r.results ?? r.symbols ?? []),
  getSymbol: (symbol: string) => req(`/api/symbols/${symbol}`),
  // One-round-trip symbol hydrate: every per-symbol section (bars, indicators,
  // buyscore, levels, ladder, supply, pbl, symbol_meta) in a single
  // response — each section is the exact body of its individual endpoint,
  // null when that section's route failed. See data/source.ts hydrate cache.
  getHydrate: (symbol: string, timeframe: string, buyscoreTimeframe?: string | null) =>
    req(`/api/symbols/${symbol}/hydrate`, { params: { timeframe, buyscoreTimeframe } }),
  putSymbol: (symbol: string, body: { isActivelyTrading: boolean; name?: string }) =>
    req(`/api/symbols/${symbol}`, { method: 'PUT', body }),

  // market data. timeframe is the SERVER token: 1Min|3Min|5Min|15Min|30Min|1H|4H|1D|1W.
  getBars: (symbol: string, timeframe = '1Min') =>
    req(`/api/symbols/${symbol}/bars`, { params: { timeframe } }).then((r) => r.bars ?? r ?? []),
  getIndicators: (symbol: string, timeframe = '1Min') =>
    req(`/api/symbols/${symbol}/indicators`, { params: { timeframe } }).then((r) => r.indicators ?? r.history ?? r ?? []),
  // The screener's forming bar, last closed bar and print watermark for one timeframe (mv5-prod/05 §3):
  // { wm: {t,k}|null, forming: bar|null, lastClosed: bar|null }, a bar = { t(ms), o, h, l, c, v, vw[, n] }.
  getForming: (symbol: string, timeframe = '1Min') =>
    req(`/api/symbols/${symbol}/forming`, { params: { timeframe } }),
  // Buy score (1-100, 50 = balanced) per bar, for the chart sub-pane. The score is
  // computed SERVICE-side from summed aggressor flow — one score per bucket, never an
  // average of scores — so the client only maps band → colour. timeframe is the
  // buy-score token: 1Min|3Min|5Min|15Min|1H. Returns [{ t(sec), v, b }].
  getBuyScore: (symbol: string, timeframe = '1Min') =>
    req(`/api/symbols/${symbol}/buyscore`, { params: { timeframe } }).then((r) => r.scores ?? []),
  getLevels: (symbol: string) =>
    req(`/api/symbols/${symbol}/levels`).then((r) => r.levels ?? []),
  // Full level response (levels[] + metadata) for the StructureRail hydrate.
  getLevelMap: (symbol: string) =>
    req(`/api/symbols/${symbol}/levels`).then((r) => ({ levels: r.levels ?? [], metadata: r.metadata ?? {}, symbol: r.symbol ?? symbol })),

  // trades / orders (scoped server-side to config.userId)
  getTrades: (q: { symbol?: string; includeOrders?: boolean } = {}) =>
    req('/api/trades', { auth: true, params: { symbol: q.symbol, includeOrders: q.includeOrders } }).then((r) => r.trades ?? r ?? []),

  // Per-symbol round-trip trade history, display-shaped server-side (fills come
  // from the orders collection — the trade doc alone lacks scale-outs). Returns
  // { symbol, open, closed, count }; hydrates the JournalCard widget.
  // Bearer required: the route 401s without the login token.
  getJournal: (symbol: string, limit = 30) =>
    req(`/api/symbols/${symbol}/journal`, { auth: true, params: { limit } }),

  // The LLM trader's console (realtime routes/agent.ts). The days the agent ran, newest first:
  // [{ date, sessionId, spendUsd, updatedAt }].
  getAgentDays: () =>
    req('/api/agent/days').then((r) => r.days ?? []),
  // One ET day of the agent's record: { date, rows, cursor, more, trips, state } — its journal rows in the order they
  // were written (`after` = the last row id held: only what is new), and its own trades with their fills.
  getAgentDay: (date: string, after?: string | null) =>
    req(`/api/agent/days/${date}`, { params: { after } }),

  // Supply card + Float & Dilution timeline model (supply_timeline collections, built
  // server-side). Returns the model; the symbol has no timeline → 404 → throws.
  getSupply: (symbol: string) =>
    req(`/api/symbols/${symbol}/supply-timeline`).then((r) => r.model ?? null),

  // recent headlines (news-service → symbols.news). Returns { items: [...] }
  // newest-first, or null when nothing has ever been stored (404).
  getNews: (symbol: string) =>
    req(`/api/symbols/${symbol}/news`).then((r) => r.news ?? null),

  // Global news-wire seed for the News rail: latest stored headline per
  // symbol, newest first, chips data (floatShares/marketCap) included.
  getNewsFeed: (params: { limit?: number } = {}) =>
    req('/api/news/feed', { params }).then((r) => r.results ?? []),

  // borrow / short-availability facts (nightly Alpaca locate sweep) plus the
  // SSR bar inputs, account snapshot and sizing params. Realtime makes no
  // broker call here; every price-derived number is computed client-side from
  // the live quote (lib/borrow.ts). { symbol, active, borrow, daily, account, params }.
  getBorrow: (symbol: string) =>
    req(`/api/symbols/${symbol}/borrow`),

  // Every locate the fleet HOLDS right now, account-wide:
  // { asOf, count, totalQty, totalRemaining, locates: [{ symbol, qty, used,
  // remaining, expiresAt }] }. A different fact from the sweep above — that one
  // says what the desk will sell next, this says what we already own and can
  // short until 20:00 ET.
  getLocates: () =>
    req('/api/locates'),

  // ladder order-flow profile (per-price volume + verdict states). Returns the
  // screener LadderEngine hydrate: { symbol, active, snapshot, signals }.
  getLadder: (symbol: string) =>
    req(`/api/symbols/${symbol}/ladder`),

  // PBL-v12 pullback signal receipts (worker PullbackPilot, SIGNAL-ONLY — these
  // are decisions the pullback engine WOULD issue, not orders). Returns
  // { symbol, active, signals } — hydrates the PblCard widget.
  getPbl: (symbol: string) =>
    req(`/api/symbols/${symbol}/pbl`),
  // Global seed for a PBL feed: today's pullback signals across the universe.
  getPblFeed: (params: { limit?: number } = {}) =>
    req('/api/pbl/feed', { params }).then((r) => r.signals ?? r.results ?? []),

  // Live risk/reward preview (worker RrPreviewPublisher, READ-ONLY — what the
  // V4 engine WOULD do at the current price, plus per-size scale-in
  // consequences while a position is open). Returns { symbol, active, frame } —
  // hydrates the RrCard widget. Spec: architecture/rr-preview-feed.md
  getRr: (symbol: string) =>
    req(`/api/symbols/${symbol}/rr`),

  // Momentum Vision detection frame (vision-service, rules-v0 — real states +
  // measured facts, no probabilities). Returns { symbol, active, frame } with
  // `active` computed server-side from the frame's own ts against a staleness
  // window. Hydrates the VisionLayer. Contract:
  // architecture/momentum-vision/vision/display.md
  // `lane` ('mv5') reads that publisher's snapshot (`worker:vision-mv5:{SYM}`)
  // and the answer carries `lane`; omitted = the unsuffixed key (MV-4), the
  // exact pre-lane request.
  getVision: (symbol: string, lane?: string) =>
    req(`/api/symbols/${symbol}/vision`, { params: { lane } }),

  // account / portfolio (Bearer required)
  getAccount: () => req('/api/account', { auth: true }),
  getPositions: () => req('/api/positions', { auth: true }),

  // Trade journal pages (Bearer required; realtime routes/journal-pages.ts). Trade ids carry '|' and ':' — encoded.
  getJournalDays: (from?: string, to?: string) => req('/api/journal/days', { auth: true, params: { from, to } }),
  getJournalDay: (date: string) => req(`/api/journal/days/${date}`, { auth: true }),
  getJournalTrade: (id: string) => req(`/api/journal/trades/${encodeURIComponent(id)}`, { auth: true }),
  putJournalOwner: (id: string, body: { grade?: string | null; note?: string | null; flag?: boolean }) =>
    req(`/api/journal/trades/${encodeURIComponent(id)}/owner`, { method: 'PUT', auth: true, body }),
  getJournalMonth: (ym: string) => req(`/api/journal/months/${ym}`, { auth: true }),
  // His entries by where they were taken (third of the 5-minute range, chasing) — the entry marks' colours.
  getEntryRecord: () => req('/api/journal/entry-record', { auth: true }),
  // One chart of a trade: a journal-builder ChartSpec (stored, else built on request), drawn by snap/SnapApp.
  getJournalChart: (id: string, tf: string, kind: 'entry' | 'exit') =>
    req(`/api/journal/trades/${encodeURIComponent(id)}/chart`, { auth: true, params: { tf, kind } }),
};
